/**
 * AI Workspace Sync Hook
 *
 * Handles bidirectional synchronization between Query Editor tabs and AI workspace files.
 * - Syncs all query tabs to AI workspace's tabs/tab_[id].sql files (v1.1 multi-tab)
 * - Syncs active tab metadata to activeTab.json
 * - Listens for AI lock/edit events and updates Query Editor
 * - Listens for AI tab file changes (created, modified, deleted, switch)
 *
 * Note: AI Workspace sync features require Pro mode. In Personal mode, the AI terminal
 * still works as a shell but workspace sync is disabled.
 */

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useTerminalStore } from '../store/terminalStore';
import { useAiLockStore } from '../store/aiLockStore';
import { useAiWorkspaceSelectionSync } from './useAiWorkspaceSelectionSync';
import { useAiExecutionRequests } from './useAiExecutionRequests';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { requestTabsClose } from '../../workspace/core/store/tabCloseGuardStore';
import { isQueryTab } from '../../workspace/core/types/workspaceTypes';
import { useAppStore } from '../../../store/appStore';
import { useHasProAccess, isProRequiredError, useProUpgradePromptStore } from '../../../core/licensing';
import type { QueryTab } from '../../workspace/core/types/workspaceTypes';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Hook to sync Query Editor tabs with AI workspace files.
 * Should be used in a top-level component (e.g., App or Workspace).
 *
 * Note: AI Workspace features require Pro mode. In Personal mode,
 * the AI terminal works as a basic shell but sync is disabled.
 */
export function useAiWorkspaceSync() {
  // Check if AI workspace features are available (Pro access - reactive)
  const canUseAiWorkspace = useHasProAccess();

  // Selection sync also needs Pro mode
  useAiWorkspaceSelectionSync();

  // Agent execute-with-confirm prompts (also Pro-gated, D-6)
  useAiExecutionRequests();

  const syncDebounceMs = useAppStore((state) => state.aiTerminalSettings.aiWorkspace.syncDebounceMs);

  // Multi-tab sync refs (v1.1)
  const tabSyncTimersRef = useRef(new Map<string, NodeJS.Timeout>());
  const lastSyncedTabsRef = useRef(new Map<string, Map<string, string>>()); // sessionId -> Map<tabId, contentHash>
  const lastSyncedActiveTabSqlRef = useRef(new Map<string, string>()); // sessionId -> activeTabSql hash ("none" when no active tab)

  // Pro-required error handling: show upgrade dialog once per session
  const showPrompt = useProUpgradePromptStore((s) => s.showPrompt);
  const proErrorShownRef = useRef(false);

  // Handle IPC result, checking for Pro-required errors
  // Shows upgrade dialog once if Pro is required but user doesn't have access
  const handleProRequiredResult = useCallback(
    <T>(result: T): boolean => {
      if (isProRequiredError(result)) {
        // Only show the dialog once per session to avoid spamming
        if (!proErrorShownRef.current) {
          proErrorShownRef.current = true;
          console.log('[AiWorkspaceSync] Pro feature required, showing upgrade dialog');
          showPrompt(result.feature);
        }
        return false;
      }
      return true;
    },
    [showPrompt]
  );

  // Get AI terminal sessions
  const sessions = useTerminalStore((s) => s.sessions);
  const aiSessions = sessions.filter((s) => s.type === 'ai');

  // Get active tab info
  const activeTabId = useWorkspaceStore((s) => s.activeTabId);
  const tabs = useWorkspaceStore((s) => s.tabs);
  const updateTabContent = useWorkspaceStore((s) => s.updateTabContent);
  const addTab = useWorkspaceStore((s) => s.addTab);
  const getTab = useWorkspaceStore((s) => s.getTab);

  // Find active query tab
  const activeTab = activeTabId ? tabs.find((t) => t.id === activeTabId) : null;
  const activeQueryTab = activeTab && isQueryTab(activeTab) ? activeTab : null;

  // Get all query tabs for multi-tab sync
  const queryTabs = useMemo(
    () => tabs.filter((t): t is QueryTab => isQueryTab(t)),
    [tabs]
  );

  // ============================================================================
  // Multi-Tab Sync (v1.1) - Sync all query tabs to tabs/tab_[id].sql
  // ============================================================================

  // Sync a single tab to its file
  const syncSingleTab = useCallback(
    (sessionId: string, tab: QueryTab) => {
      if (!canUseAiWorkspace) return;
      if (!window.electronAPI?.aiWorkspace) return;

      const timerKey = `${sessionId}:${tab.id}`;

      // Cancel existing timer
      const existingTimer = tabSyncTimersRef.current.get(timerKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Schedule debounced sync
      const timer = setTimeout(() => {
        tabSyncTimersRef.current.delete(timerKey);

        // Check if content changed since last sync
        const sessionTabHashes = lastSyncedTabsRef.current.get(sessionId) ?? new Map<string, string>();
        const contentHash = `${tab.id}:${tab.content.query.length}:${tab.content.query.slice(0, 100)}`;
        if (sessionTabHashes.get(tab.id) === contentHash) {
          return; // Content unchanged
        }

        // Update last synced hash
        sessionTabHashes.set(tab.id, contentHash);
        lastSyncedTabsRef.current.set(sessionId, sessionTabHashes);

        // Sync to file
        window.electronAPI?.aiWorkspace.syncTabFile(sessionId, tab.id, tab.content.query)
          .then((result) => {
            handleProRequiredResult(result);
          })
          .catch((err) => {
            console.error(`[AiWorkspaceSync] Failed to sync tab ${tab.id} to session ${sessionId}:`, err);
          });
      }, syncDebounceMs);

      tabSyncTimersRef.current.set(timerKey, timer);
    },
    [canUseAiWorkspace, syncDebounceMs, handleProRequiredResult]
  );

	  // Sync active tab metadata to activeTab.json
	  const syncActiveTabJson = useCallback(
	    (sessionId: string, tab: QueryTab | null) => {
	      if (!canUseAiWorkspace) return;
	      if (!window.electronAPI?.aiWorkspace) return;

	      if (tab) {
	        window.electronAPI.aiWorkspace.syncActiveTabJson(sessionId, tab.id, tab.title)
	          .then((result) => {
	            handleProRequiredResult(result);
	          })
	          .catch((err) => {
	            console.error(`[AiWorkspaceSync] Failed to sync active tab json:`, err);
	          });
	      }
	    },
	    [canUseAiWorkspace, handleProRequiredResult]
	  );

	  // Sync legacy activeTab.sql (single-tab compat mode)
	  const syncActiveTabSql = useCallback(
	    (sessionId: string, tab: QueryTab | null) => {
	      if (!canUseAiWorkspace) return;
	      if (!window.electronAPI?.aiWorkspace) return;

	      window.electronAPI.aiWorkspace
	        .syncActiveTabSql(sessionId, tab ? tab.content.query : null)
	        .then((result) => {
	          handleProRequiredResult(result);
	        })
	        .catch((err) => {
	          console.error('[AiWorkspaceSync] Failed to sync activeTab.sql:', err);
	        });
	    },
	    [canUseAiWorkspace, handleProRequiredResult]
	  );

  // Delete tab file when tab is closed
  const deleteTabFileForSession = useCallback(
    (sessionId: string, tabId: string) => {
      if (!canUseAiWorkspace) return;
      if (!window.electronAPI?.aiWorkspace) return;

      // Cancel pending sync timer
      const timerKey = `${sessionId}:${tabId}`;
      const existingTimer = tabSyncTimersRef.current.get(timerKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
        tabSyncTimersRef.current.delete(timerKey);
      }

      // Clear hash tracking
      const sessionTabHashes = lastSyncedTabsRef.current.get(sessionId);
      if (sessionTabHashes) {
        sessionTabHashes.delete(tabId);
      }

      // Delete the file
      window.electronAPI.aiWorkspace.deleteTabFile(sessionId, tabId)
        .then((result) => {
          handleProRequiredResult(result);
        })
        .catch((err) => {
          console.error(`[AiWorkspaceSync] Failed to delete tab file ${tabId}:`, err);
        });
    },
    [canUseAiWorkspace, handleProRequiredResult]
  );

  // Track previous query tabs to detect closures
  const prevQueryTabsRef = useRef<QueryTab[]>([]);

  // Effect to sync all query tabs
  useEffect(() => {
    if (!canUseAiWorkspace) return;
    if (aiSessions.length === 0) return;

    const prevTabIds = new Set(prevQueryTabsRef.current.map((t) => t.id));
    const currentTabIds = new Set(queryTabs.map((t) => t.id));

    // Detect closed tabs
    const closedTabIds = [...prevTabIds].filter((id) => !currentTabIds.has(id));

	    // For each AI session, sync all tabs and delete closed ones
	    for (const session of aiSessions) {
      // Delete files for closed tabs
      for (const tabId of closedTabIds) {
        deleteTabFileForSession(session.id, tabId);
      }

      // Sync all current tabs
      for (const tab of queryTabs) {
        syncSingleTab(session.id, tab);
      }

		      // Sync active tab metadata + legacy activeTab.sql (only when the active tab state changes)
		      const nextActiveHash = activeQueryTab
		        ? `${activeQueryTab.id}:${activeQueryTab.content.query.length}:${activeQueryTab.content.query.slice(0, 100)}`
		        : 'none';
		      if (lastSyncedActiveTabSqlRef.current.get(session.id) !== nextActiveHash) {
		        syncActiveTabJson(session.id, activeQueryTab);
		        syncActiveTabSql(session.id, activeQueryTab);
		        lastSyncedActiveTabSqlRef.current.set(session.id, nextActiveHash);
		      }
		    }

    // Update prev tabs ref
    prevQueryTabsRef.current = queryTabs;

    // Copy ref to local variable for cleanup (React hooks lint rule)
    const timersMap = tabSyncTimersRef.current;

    // Cleanup on unmount
    return () => {
      // Clear all pending timers
      for (const timer of timersMap.values()) {
        clearTimeout(timer);
      }
      timersMap.clear();
    };
	  }, [
	    canUseAiWorkspace,
	    aiSessions,
	    queryTabs,
	    activeQueryTab,
	    syncSingleTab,
	    syncActiveTabJson,
	    syncActiveTabSql,
	    deleteTabFileForSession,
	  ]);

  // Get lock store actions
  const setLock = useAiLockStore((s) => s.setLock);
  const removeLock = useAiLockStore((s) => s.removeLock);

  // Listen for IPC events from main process
  // Only listen when user has Pro mode - workspace sync is a Pro feature
  useEffect(() => {
    // Skip event listeners if not Pro - prevents workspace sync in Personal mode
    if (!canUseAiWorkspace) return;
    if (!window.electronAPI?.aiWorkspace) return;

    const cleanups: (() => void)[] = [];

    // ============================================================================
    // Multi-Tab Event Listeners (v1.1)
    // ============================================================================

    // Handle tab file created - AI created a new tab in tabs/tab_[id].sql
    // Note: chokidar may report 'add' for a file that was already synced if the
    // modification happens before the initial 'add' event is emitted (e.g. with
    // polling-based watchers). In that case, update the existing tab instead.
    const unsubTabFileCreated = window.electronAPI.aiWorkspace.onTabFileCreated((event) => {
      console.log('[AiWorkspaceSync] Tab file created by AI:', event);

      // Check if tab already exists (chokidar may report 'add' for a modified file)
      const existingTab = getTab(event.tabId);
      if (existingTab && isQueryTab(existingTab)) {
        updateTabContent(event.tabId, { query: event.content });
        console.log(`[AiWorkspaceSync] Updated existing tab ${event.tabId} from AI tab file (add event)`);
        return;
      }

      const connectionId = event.connectionId;
      const database = event.database;

      const connectionInfo = connectionId
        ? (() => {
            const { activeConnections, connections } = useConnectionStore.getState();
            const activeConnection = activeConnections.find((conn) => conn.id === connectionId);
            const savedConnection = connections.find((conn) => conn.id === connectionId);

            const connectionName = safeConnectionName(savedConnection ?? activeConnection?.connection, 'Unnamed Connection');
            const serverHost = safeConnectionServer(activeConnection?.connection);
            const resolvedDatabase = database ?? activeConnection?.connection.database;

            return {
              connectionId,
              connectionName,
              serverHost,
              database: resolvedDatabase,
            };
          })()
        : undefined;

      // Create a new query tab with AI's content
      // Use the tab ID from the file (allows AI to reference it)
      const newTabId = addTab({
        type: 'query',
        title: `AI Tab`,
        connectionInfo,
        content: { query: event.content },
      });
      console.log(`[AiWorkspaceSync] Created new tab ${newTabId} from AI tab file ${event.tabId}`);
    });
    cleanups.push(unsubTabFileCreated);

    // Handle tab file changed - AI modified content of an existing tab
    const unsubTabFileChanged = window.electronAPI.aiWorkspace.onTabFileChanged((event) => {
      console.log('[AiWorkspaceSync] Tab file changed by AI:', event);

      // Find the tab by ID
      const tab = getTab(event.tabId);
      if (tab && isQueryTab(tab)) {
        updateTabContent(event.tabId, { query: event.content });
        console.log(`[AiWorkspaceSync] Updated tab ${event.tabId} content from AI`);
      } else {
        console.warn(`[AiWorkspaceSync] Tab ${event.tabId} not found for AI content update`);
      }
    });
    cleanups.push(unsubTabFileChanged);

    // Handle tab file deleted - AI deleted a tab
    const unsubTabFileDeleted = window.electronAPI.aiWorkspace.onTabFileDeleted((event) => {
      console.log('[AiWorkspaceSync] Tab file deleted by AI:', event);

      // Close the tab if it exists
      const tab = getTab(event.tabId);
      if (tab) {
        // Same guard as a user's close: an open or starting transaction asks first.
        requestTabsClose([event.tabId], (kept) => {
          if (!kept.has(event.tabId)) useWorkspaceStore.getState().removeTab(event.tabId);
        });
        console.log(`[AiWorkspaceSync] Closing tab ${event.tabId} (deleted by AI)`);
      }
    });
    cleanups.push(unsubTabFileDeleted);

	    // Handle active tab switch - AI requested to switch active tab
	    const unsubActiveTabSwitch = window.electronAPI.aiWorkspace.onActiveTabSwitch((event) => {
	      console.log('[AiWorkspaceSync] Active tab switch requested by AI:', event);

      // Switch to the requested tab if it exists
      const tab = getTab(event.tabId);
      if (tab) {
        useWorkspaceStore.getState().setActiveTab(event.tabId);
        console.log(`[AiWorkspaceSync] Switched to tab ${event.tabId} (requested by AI)`);
      } else {
        console.warn(`[AiWorkspaceSync] Tab ${event.tabId} not found for AI switch request`);
      }
	    });
	    cleanups.push(unsubActiveTabSwitch);

	    // Handle legacy activeTab.sql changes (single-tab compat mode)
	    const unsubActiveTabSqlChanged = window.electronAPI.aiWorkspace.onActiveTabSqlChanged((event) => {
	      console.log('[AiWorkspaceSync] activeTab.sql changed:', event);

	      const state = useWorkspaceStore.getState();
	      const currentActiveId = state.activeTabId;
	      const currentTabs = state.tabs;
	      const currentActive = currentActiveId ? currentTabs.find((t) => t.id === currentActiveId) : null;

	      if (currentActive && isQueryTab(currentActive)) {
	        updateTabContent(currentActive.id, { query: event.content });
	        return;
	      }

	      addTab({
	        type: 'query',
	        title: 'AI Tab',
	        content: { query: event.content },
	      });
	    });
	    cleanups.push(unsubActiveTabSqlChanged);

    // ============================================================================
    // Per-Tab Meta Lock Event Listeners (v1.1)
    // ============================================================================

    // Handle tab meta changed - AI created/updated a .meta file (per-tab lock)
    const unsubTabMetaChanged = window.electronAPI.aiWorkspace.onTabMetaChanged((event) => {
      console.log('[AiWorkspaceSync] Tab meta changed:', event);

      const { sessionId, tabId, meta } = event;

      // Only process if status is 'editing' (locked state)
      if (meta.status === 'editing') {
        // Update lock store with the new per-tab lock
        setLock(tabId, {
          sessionId,
          tabId,
          lockedAt: meta.lockedAt,
          lastActivity: meta.lastActivity ?? meta.lockedAt,
          message: meta.message,
          abortRequested: meta.abortRequested,
          isMetaLock: true,
        });
        console.log(`[AiWorkspaceSync] Tab ${tabId} locked by AI session ${sessionId}`);
      } else if (meta.status === 'idle') {
        // Status changed to idle - unlock the tab
        removeLock(tabId);
        console.log(`[AiWorkspaceSync] Tab ${tabId} unlocked (status: idle)`);
      }
    });
    cleanups.push(unsubTabMetaChanged);

    // Handle tab meta removed - AI deleted the .meta file (unlock)
    const unsubTabMetaRemoved = window.electronAPI.aiWorkspace.onTabMetaRemoved((event) => {
      console.log('[AiWorkspaceSync] Tab meta removed:', event);

      const { tabId } = event;
      removeLock(tabId);
      console.log(`[AiWorkspaceSync] Tab ${tabId} unlocked (meta file removed)`);
    });
    cleanups.push(unsubTabMetaRemoved);

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [canUseAiWorkspace, getTab, updateTabContent, addTab, setLock, removeLock]);
}
